import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileCode, X, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../supabase/client';
import { decode } from 'base64-arraybuffer';

interface CodingFileUploadProps {
  questionId: string;
  paperId?: string;
  paperTitle?: string;
  title?: string;              // 题目名称
  userId?: string;
  questionType: 'python_coding' | 'cpp_coding' | 'scratch_coding';
  onFileUpload: (fileUrl: string, fileName: string) => void;
  disabled?: boolean;
  existingFileUrl?: string;
}

const FILE_TYPE_CONFIG: Record<string, { extensions: string[]; maxSize: number; label: string }> = {
  python_coding: {
    extensions: ['.py'],
    maxSize: 5 * 1024 * 1024, // 5MB
    label: 'Python 文件 (.py)'
  },
  cpp_coding: {
    extensions: ['.cpp', '.cc', '.cxx', '.c'],
    maxSize: 5 * 1024 * 1024, // 5MB
    label: 'C++ 文件 (.cpp, .cc, .cxx, .c)'
  },
  scratch_coding: {
    extensions: ['.sb3'],
    maxSize: 10 * 1024 * 1024, // 10MB
    label: 'Scratch 文件 (.sb3)'
  }
};

export default function CodingFileUpload({
  questionId,
  paperId,
  paperTitle,
  title,
  userId,
  questionType,
  onFileUpload,
  disabled = false,
  existingFileUrl
}: CodingFileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; url: string } | null>(
    existingFileUrl ? { name: existingFileUrl.split('/').pop() || '已上传文件', url: existingFileUrl } : null
  );
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 当 existingFileUrl prop 变化时，同步更新内部状态（防止组件复用导致状态污染）
  useEffect(() => {
    if (existingFileUrl) {
      setUploadedFile({ name: existingFileUrl.split('/').pop() || '已上传文件', url: existingFileUrl });
    } else {
      setUploadedFile(null);
    }
  }, [existingFileUrl]);

  const config = FILE_TYPE_CONFIG[questionType];

  const validateFile = (file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!config.extensions.includes(ext)) {
      return `不支持的文件类型，请上传 ${config.label}`;
    }
    if (file.size > config.maxSize) {
      return `文件大小超过限制，最大允许 ${config.maxSize / 1024 / 1024}MB`;
    }
    return null;
  };

  const uploadFile = async (file: File) => {
    setError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    try {
      // 生成文件路径: 使用试卷名称和题目名称作为文件夹结构
      const timestamp = Date.now();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      // 清理试卷名称和题目名称，用于文件夹路径（移除特殊字符，限制长度）
      const safePaperTitle = (paperTitle || paperId || 'custom')
        .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '_')
        .substring(0, 50);
      const safeQuestionTitle = (title || questionId || 'question')
        .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '_')
        .substring(0, 50);
      const filePath = `${safePaperTitle}/${safeQuestionTitle}/${userId || 'anonymous'}/${timestamp}_${safeFileName}`;

      // 必须使用 ArrayBuffer 上传（base64-arraybuffer），直接传 File 对象会导致 400 错误
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );
      const decoded = decode(base64);

      const { data, error: uploadError } = await supabase.storage
        .from('coding-answers')
        .upload(filePath, decoded, {
          contentType: file.type || 'application/octet-stream',
          upsert: true
        });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      const { data: urlData } = supabase.storage
        .from('coding-answers')
        .getPublicUrl(filePath);

      const fileUrl = urlData.publicUrl;
      setUploadedFile({ name: file.name, url: fileUrl });
      onFileUpload(fileUrl, file.name);
    } catch (err: any) {
      setError(err.message || '上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || uploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    onFileUpload('', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (disabled && uploadedFile) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">已提交文件</p>
            <p className="text-xs text-green-600">{uploadedFile.name}</p>
          </div>
          <a
            href={uploadedFile.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            查看文件
          </a>
        </div>
      </div>
    );
  }

  if (uploadedFile) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <FileCode className="w-5 h-5 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">已选择文件</p>
            <p className="text-xs text-blue-600">{uploadedFile.name}</p>
          </div>
          {!disabled && (
            <button
              onClick={handleRemoveFile}
              className="p-1 hover:bg-blue-100 rounded transition-colors"
            >
              <X className="w-4 h-4 text-blue-600" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          文件已上传，提交试卷时将保存此答案
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-all duration-200
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={config.extensions.join(',')}
          onChange={handleFileSelect}
          disabled={disabled || uploading}
          className="hidden"
        />

        {uploading ? (
          <div className="space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600">正在上传...</p>
          </div>
        ) : (
          <>
            <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
            <p className="text-sm font-medium text-gray-700 mb-1">
              点击或拖拽文件到此处上传
            </p>
            <p className="text-xs text-gray-500">
              支持 {config.label}，最大 {config.maxSize / 1024 / 1024}MB
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}
